import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { Session } from '#src/shared/session';

import type { AiSummaries } from './aiSummaries';
import { getAiSummaries } from './aiSummaries';
import { isDecisionMaker } from './decisionMaker';
import { fetchPetitionDetailsExperiments } from './experiments';
import type { PetitionDetailsDataExperimentsQuery } from './experiments.graphql';
import { getFullAddressCollectionFilters } from './fullAddressCollectionFilters';
import { isGdprConsentProvided } from './gdprConsentProvided';
import { normalizePetitionSignFormFullAddressCollectionRollout } from './normalize/normalizePetitionSignFormFullAddressCollectionRollout';
import { normalizeSupporterGrowthEmbedSignPetitionFormExperiment } from './normalize/normalizeSupporterGrowthEmbedSignPetitionFormExperiment';
import type { PetitionViewerInfo } from './petitionViewerInfo';
import { getPetitionViewerInfo } from './petitionViewerInfo';
import { getRecentUserPetition } from './recentUserPetition';

type Experiments = PetitionDetailsDataExperimentsQuery['experiments'];
type SupporterGrowthEmbedSignPetitionFormExperiment = Experiments['supporterGrowthEmbedSignPetitionFormExperiment'];
export type SupporterGrowthEmbedSignPetitionFormExperimentVariation =
	SupporterGrowthEmbedSignPetitionFormExperiment['variation'];

export type PetitionDetailsPageUserData = PetitionViewerInfo & {
	userId: string | undefined;
	aiSummaries: AiSummaries | null;
	decisionMaker: boolean;
	gdprConsentProvided: boolean;
	signatureThermometerEnabled: boolean;
	isSunriseEnabled: boolean;
	fullSunrisePetitionPageShareComponentExperiment: boolean;
	petitionPageMomentumBarExperiment: 'control' | 'enabled';
	supporterGrowthEmbedSignPetitionFormExperimentVariation: SupporterGrowthEmbedSignPetitionFormExperimentVariation;
	petitionSignFormFullAddressCollectionRolloutVariation: 'control' | 'fake_control' | 'v1';
	starterDashboardPromptingBannerEnabled: boolean;
	petitionPageStarterInHeaderExperimentEnabled: boolean;
	experiments: Omit<
		Experiments,
		| 'sunriseSupportersV1'
		| 'supporterShareVisualsExperiment'
		| 'signatureThermometerExperimentDesktop'
		| 'fullSunrisePetitionPageShareComponentExperiment'
		| 'petitionPageMomentumBarExperiment'
		| 'supporterGrowthEmbedSignPetitionFormExperiment'
		| 'petitionSignFormFullAddressCollectionRollout'
		| 'petitionPageStarterInHeaderExperiment'
	>;
};

export async function getPetitionDetailsPageUserData(
	utilityContext: UtilityContext,
	slugOrId: string,
	{
		currentUserId,
		loginState,
		decisionMakerHash,
	}: {
		currentUserId: string | undefined;
		loginState: Session['loginState'] | undefined;
		decisionMakerHash?: string;
		hasShowLegislativeBodiesParam: boolean;
	},
): Promise<PetitionDetailsPageUserData> {
	const isGuest = loginState ? loginState === 'GUEST' : true;
	const [
		{ petitionId, ...petitionViewerInfo },
		aiSummaries,
		decisionMaker,
		gdprConsentProvided,
		recentPetition,
		fullAddressCollectionFilters,
		experiments,
	] = await Promise.all([
		getPetitionViewerInfo(utilityContext, slugOrId, currentUserId),
		getAiSummaries(utilityContext, { slugOrId }),
		!isGuest || decisionMakerHash
			? isDecisionMaker(utilityContext, { slugOrId, decisionMakerHash })
			: Promise.resolve(false),
		isGdprConsentProvided(utilityContext),
		isGuest ? Promise.resolve(undefined) : getRecentUserPetition(utilityContext),
		getFullAddressCollectionFilters(utilityContext, slugOrId),
		fetchPetitionDetailsExperiments(utilityContext),
	]);

	const {
		sunriseSupportersV1,
		signatureThermometerExperimentDesktop,
		fullSunrisePetitionPageShareComponentExperiment,
		petitionPageMomentumBarExperiment,
		petitionSignFormFullAddressCollectionRollout,
		petitionPageStarterInHeaderExperiment,
		...experimentsRest
	} = experiments;

	const supporterGrowthEmbedSignPetitionFormExperimentVariation =
		await normalizeSupporterGrowthEmbedSignPetitionFormExperiment({
			experiment: experiments.supporterGrowthEmbedSignPetitionFormExperiment,
			utilityContext,
		});

	const petitionSignFormFullAddressCollectionRolloutVariation =
		await normalizePetitionSignFormFullAddressCollectionRollout({
			experiment: petitionSignFormFullAddressCollectionRollout,
			filters: fullAddressCollectionFilters,
			loginState,
			utilityContext,
		});

	return {
		...petitionViewerInfo,
		userId: currentUserId,
		aiSummaries,
		decisionMaker,
		gdprConsentProvided,
		signatureThermometerEnabled: signatureThermometerExperimentDesktop.variation === 'v1',
		isSunriseEnabled: sunriseSupportersV1.variation === 'enabled',
		fullSunrisePetitionPageShareComponentExperiment:
			fullSunrisePetitionPageShareComponentExperiment.variation === 'enabled',
		petitionPageMomentumBarExperiment: petitionPageMomentumBarExperiment.variation,
		supporterGrowthEmbedSignPetitionFormExperimentVariation,
		petitionSignFormFullAddressCollectionRolloutVariation,
		starterDashboardPromptingBannerEnabled: isStarterDashboardPromptingBannerEnabled({
			petitionId,
			ownPetition: petitionViewerInfo.ownPetition,
			recentPetition,
		}),
		petitionPageStarterInHeaderExperimentEnabled: petitionPageStarterInHeaderExperiment.variation === 'enabled',
		experiments: experimentsRest,
	};
}

function isStarterDashboardPromptingBannerEnabled({
	petitionId,
	ownPetition,
	recentPetition,
}: {
	petitionId?: string;
	ownPetition: boolean;
	recentPetition: { id: string } | undefined;
}) {
	if (!recentPetition || !petitionId) return false;
	if (!ownPetition) return false;
	if (recentPetition.id !== petitionId) return false;
	return true;
}
